import { type FC, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CreateOrganizationComponent } from "@/routes/create-organization/CreateOrganizationComponent";
import { type PlainOrganization, type PlainOrganizationMember } from "@/queries/organization-queries";
import { setPrincipal } from "@/principal";
import { ClosableWithEsc, JustifiedContentColumn } from "@/components/CloseableFullPage";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { Button } from "@/components/flexkit/Button";
import { OnaLettermark } from "@/assets/icons/OnaLettermark";

export const CreateOrganizationPage: FC = () => {
    useDocumentTitle("Create Organization");

    const navigate = useNavigate();
    const location = useLocation();

    const onSuccess = useCallback(
        (organization: PlainOrganization, member: PlainOrganizationMember) => {
            setPrincipal(member.userId);
            navigate("/onboarding/ona/", { replace: true, state: { organizationId: organization.id } });
        },
        [navigate],
    );

    const { onClose, onCloseLabel } = useMemo(() => {
        // We we're rendering this page coming from JoinOrganization then "Back"/<kbd>Escape</kbd> should navigate back.
        if (location?.state?.from == "join") {
            return {
                onClose: () => navigate(-1),
                onCloseLabel: "Back",
            };
        }

        return {
            onClose: () => navigate("/"),
            onCloseLabel: "Back",
        };
    }, [location?.state?.from, navigate]);

    return (
        <ClosableWithEsc onClose={onClose} data-testid="create-organization-page" className="h-full">
            <JustifiedContentColumn>
                <div className="flex w-full flex-col items-center gap-2">
                    <OnaLettermark className="mb-2" />
                    <CreateOrganizationComponent onSuccess={onSuccess} />
                    <Button
                        variant="link"
                        onClick={onClose}
                        data-testid="back-button"
                        data-tracking-id="back-create-organization-page"
                    >
                        {onCloseLabel}
                    </Button>
                </div>
            </JustifiedContentColumn>
        </ClosableWithEsc>
    );
};
